"use client";

import { useVenueDetail } from "@/hooks/venueContext";
import { DEFAULT_IMAGES } from "@/lib/constants";
import { useEffect, useState } from "react";
import S3Image from "@/components/S3Image";
import unescapeHTML from "@/lib/unescape-html";
import AvatarDecoration from "@/components/views/avatar-decoration/AvatarDecoration";
import WhatsappIcon from "@/components/hero-icons/whatsapp-icon";
import WhatsappLinkWithBeacon from "@/components/WhatsappLinkWithBeacon";
import useGetWpHref from "@/hooks/useGetWpHref";
import useWhatsappContext from "@/hooks/useWhatsappContext";
import getTopDiscountByVenue from "@/lib/get-top-discount-by-venue";
import { useTranslation } from "@/hooks/translationContext";
import { useIsAskQuestionEnabled } from "@/hooks/useIsAskQuestionEnabled";
import { isPhysiotherapistVenue } from "@/lib/venue-business-type";
import {
  WHATSAPP_SECTIONS,
  WHATSAPP_TAGS,
} from "@/@core/enums/whatsapp_tags.enum";

interface Props {
  onShareClick?: () => void;
  onContactClick?: () => void;
  variant?: "desktop" | "mobile";
  /**
   * Which part of the mobile header to render. The title row sits above the
   * photos while the action buttons stay below them, so the two halves are
   * rendered from different places in the header layout.
   */
  section?: "title" | "actions" | "all";
}

// Share Icon
function ShareButtonIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.19071C7.54305 8.46487 6.6582 8 5.66667 8C4.19391 8 3 9.34315 3 11C3 12.6569 4.19391 14 5.66667 14C6.6582 14 7.54305 13.5351 8.08261 12.8093L15.0227 16.6294C15.0077 16.7508 15 16.8745 15 17C15 18.6569 16.3431 20 18 20C19.6569 20 21 18.6569 21 17C21 15.3431 19.6569 14 18 14C17.0085 14 16.1237 14.4649 15.5841 15.1907L8.64401 11.3706C8.65903 11.2492 8.66667 11.1255 8.66667 11C8.66667 10.8745 8.65903 10.7508 8.64401 10.6294L15.5841 6.80929C16.1237 7.53513 17.0085 8 18 8Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Question/Comment Icon
function QuestionIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 13.5997 2.37562 15.1116 3.04346 16.4525C3.22094 16.8088 3.28001 17.2161 3.17712 17.6006L2.58151 19.8267C2.32295 20.793 3.20701 21.677 4.17335 21.4185L6.39939 20.8229C6.78393 20.72 7.19121 20.7791 7.54753 20.9565C8.88837 21.6244 10.4003 22 12 22Z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M12 12V12.01"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M9.5 9C9.5 7.61929 10.6193 6.5 12 6.5C13.3807 6.5 14.5 7.61929 14.5 9C14.5 10.3807 13.3807 11.5 12 11.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

// Phone Icon
function PhoneButtonIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 4H9L11 9L8.5 10.5C9.57096 12.6715 11.3285 14.429 13.5 15.5L15 13L20 15V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21C14.0993 20.763 10.4202 19.1065 7.65683 16.3432C4.8935 13.5798 3.23705 9.90074 3 6C3 5.46957 3.21071 4.96086 3.58579 4.58579C3.96086 4.21071 4.46957 4 5 4Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function VenueDetailHeaderInfo({
  onShareClick,
  onContactClick,
  variant,
  section = "all",
}: Props) {
  const { t } = useTranslation();
  const { venue } = useVenueDetail();
  const isAskQuestionEnabled = useIsAskQuestionEnabled();
  // Physiotherapist venues have no comment / question section (SAB-362).
  const showsAskQuestion = isAskQuestionEnabled && !isPhysiotherapistVenue(venue);
  const getWhatsappHrefUrl = useGetWpHref();
  const whatsappContext = useWhatsappContext();
  const [imageSrc, setImageSrc] = useState(`venue/${venue?.id}/${venue?.logo}`);

  useEffect(() => {
    setImageSrc(`venue/${venue?.id}/${venue?.logo}`);
  }, [venue]);

  if (!venue) return null;

  const venueTitle = unescapeHTML(venue?.descriptions?.[0]?.title || "");

  const whatsappHref = venue?.whatsappPhoneNumber
    ? getWhatsappHrefUrl({
        tag: WHATSAPP_TAGS.NORMAL,
        phone: venue.whatsappPhoneNumber,
        translateValues: whatsappContext.getContext({
          venue: venueTitle,
          "discount-visitors": getTopDiscountByVenue(venue as Venue.List),
        }),
      })
    : "";

  return (
    <>
      {/* Desktop Version */}
      {variant !== "mobile" && (
      <div className="flex items-center gap-3">
        {/* Logo */}
        <div className="rounded-[16px] overflow-hidden border border-white/30 shadow-sm">
          <AvatarDecoration>
            <S3Image
              width={46}
              height={46}
              sizes="46px"
              loading="eager"
              src={venue?.logo ? imageSrc : DEFAULT_IMAGES.VENUE_LOGO}
              alt={venueTitle}
              title={venueTitle}
              onError={() => {
                setImageSrc(DEFAULT_IMAGES.VENUE_LOGO);
              }}
              className="rounded-[16px] w-[46px] h-[46px] object-contain"
            />
          </AvatarDecoration>
        </div>

        <div className="flex flex-col gap-1">
          {/* Title - the page's single h1 on desktop, same as the mobile header */}
          <div className="flex items-center gap-1.5">
            <h1
              className="font-bold text-[15.9px] text-[#1e293b] line-clamp-1 overflow-hidden"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {venueTitle}
            </h1>
          </div>
        </div>
      </div>
      )}

      {/* Mobile Title Row with WhatsApp - sits above the photos */}
      {variant === "mobile" && section !== "actions" && (
        <div className="flex items-center gap-2 px-4 py-3">
          <h1
            className="font-semibold text-[18px] text-black tracking-[-0.18px] truncate flex-1"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {venueTitle}
          </h1>

          {/* WhatsApp Icon - smaller icon with larger bg area */}
          {venue?.whatsappPhoneNumber && (
            <WhatsappLinkWithBeacon
              title={t("venue-detail-contact-button-title", { venue: venueTitle })}
              section={WHATSAPP_SECTIONS.VENUE_DETAIL}
              venueId={venue?.id}
              href={whatsappHref}
              className="flex-shrink-0 size-9 rounded-full bg-[#25D366] flex items-center justify-center"
            >
              <WhatsappIcon className="size-[18px] text-white" />
            </WhatsappLinkWithBeacon>
          )}
        </div>
      )}

      {/* Mobile Action Buttons - stay below the photos */}
      {variant === "mobile" && section !== "title" && (
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={onShareClick}
            className="flex-1 flex items-center justify-center gap-2 bg-white border border-[#d9d9d9] rounded-[9px] py-2.5 shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
          >
            <ShareButtonIcon className="size-5 text-[#6f6f6f]" />
            <span
              className="text-[12px] font-medium text-[#6f6f6f] tracking-[-0.12px]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              Paylaş
            </span>
          </button>

          {showsAskQuestion && (
            <button
              onClick={() => {
                const element = document.getElementById("comments");
                if (element) {
                  const isMobile = window.innerWidth < 768;
                  const offset = isMobile ? 100 : 140;
                  const elementPosition =
                    element.getBoundingClientRect().top + window.scrollY;
                  window.scrollTo({
                    top: elementPosition - offset,
                    behavior: "smooth",
                  });
                }
              }}
              className="flex-1 flex items-center justify-center gap-2 bg-white border border-[#d9d9d9] rounded-[9px] py-2.5 shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
            >
              <QuestionIcon className="size-5 text-[#6f6f6f]" />
              <span
                className="text-[12px] font-medium text-[#6f6f6f] tracking-[-0.12px]"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Soru Sorun
              </span>
            </button>
          )}

          <button
            onClick={onContactClick}
            className="flex-1 flex items-center justify-center gap-2 bg-[#20b038] border border-[#d9d9d9] rounded-[9px] py-2.5 shadow-[0px_0px_2.4px_0px_rgba(99,99,99,0.25)]"
          >
            <PhoneButtonIcon className="size-5 text-white" />
            <span
              className="text-[12px] font-medium text-white tracking-[-0.12px]"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              İletişim
            </span>
          </button>
        </div>
      )}
    </>
  );
}
